import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, TrendingUp, Wallet, CalendarDays, CalendarRange, HandCoins, Receipt, PiggyBank, AlertCircle, Landmark, Car, Gauge, Clock, Eraser } from "lucide-react";
import { API, authHeaders, money } from "./shared";

function Metric({ label, value, icon: Icon, tone = "default", sub, testid }) {
  const tones = { default: "text-white", ok: "text-green-400", warn: "text-yellow-400", danger: "text-red-400", gold: "text-[#F2B705]" };
  return (
    <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-1.5 text-white/45 text-[11px] uppercase tracking-wider"><Icon size={13} /> {label}</div>
      <div className={`font-display font-black text-2xl mt-1.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-white/35 text-xs mt-0.5">{sub}</div>}
    </div>
  );
}

export default function FinanceDashboard({ token, onAuthError }) {
  const [d, setD] = useState(null);
  const [trace, setTrace] = useState(null);
  const openTrace = async (period) => {
    try {
      const { data } = await axios.get(`${API}/finance/revenue-trace?period=${period}`, authHeaders(token));
      setTrace(data);
    } catch { toast.error("Could not load the revenue trace."); }
  };

  const reverseFromTrace = async (r) => {
    if (!window.confirm(`Reverse this ${money(r.amount)} payment (${r.customer || "—"})?\n\n⚠ This corrects an internal payment entry. It does NOT send money to or withdraw money from the customer.\n\nThe record stays visible as REVERSED — NOT COUNTED and all revenue totals update immediately.`)) return;
    const reason = window.prompt("Reversal reason (required — written to the audit trail):");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reversal reason is required."); return; }
    try {
      await axios.post(`${API}/finance/ledger/${r.ledger_id}/reverse`, { reason: reason.trim() }, authHeaders(token));
      toast.success(`${money(r.amount)} payment REVERSED — NOT COUNTED. No customer refund was issued.`);
      await openTrace(trace.period); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reverse the payment."); }
  };

  const undoFromTrace = async (r) => {
    if (!window.confirm(`Undo the reversal on this ${money(r.amount)} payment (${r.customer || "—"})?\n\nThe same original payment is reactivated and its allocation restored exactly — no new transaction is created.`)) return;
    const reason = window.prompt("Reason for undoing the reversal (required):");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required."); return; }
    try {
      await axios.post(`${API}/finance/ledger/${r.ledger_id}/undo-reverse`, { reason: reason.trim() }, authHeaders(token));
      toast.success(`Reversal undone — the ${money(r.amount)} payment counts again.`);
      await openTrace(trace.period); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not undo the reversal."); }
  };
  const [loading, setLoading] = useState(true);

  const load = () => {
    axios.get(`${API}/finance/dashboard`, authHeaders(token)).then((r) => setD(r.data))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const zeroBalances = async () => {
    if (!window.confirm("Zero out EVERY booking balance (owed AND credits)? Each booking's total is adjusted to match what was actually collected — payment history is never touched. This cannot be undone.")) return;
    const pwd = window.prompt("Management only — enter the management password to confirm:");
    if (!pwd) return;
    try {
      const { data } = await axios.post(`${API}/finance/zero-balances`, { management_password: pwd }, authHeaders(token));
      toast.success(`${data.zeroed} balance${data.zeroed !== 1 ? "s" : ""} zeroed — $${Number(data.owed_cleared).toLocaleString(undefined, { minimumFractionDigits: 2 })} owed cleared, $${Number(data.credits_cleared).toLocaleString(undefined, { minimumFractionDigits: 2 })} in credits cleared.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not zero balances."); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  if (!d) return <p className="text-white/40">Could not load dashboard.</p>;

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      <div>
        <h3 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-2">Revenue</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["today", "fd-revenue-today", "Revenue Today", d.revenue_today, CalendarDays],
            ["week", "fd-revenue-week", "This Week", d.revenue_week, CalendarRange],
            ["month", "fd-revenue-month", "This Month", d.revenue_month, TrendingUp],
            ["year", "fd-revenue-year", "This Year", d.revenue_year, Wallet]].map(([p, tid, label, val, Icon]) => (
            <button key={p} data-testid={tid} onClick={() => openTrace(p)} title="Click to see the exact transactions behind this number"
              className="text-left cursor-pointer rounded-xl transition-transform hover:scale-[1.02]">
              <Metric label={label} value={money(val)} icon={Icon} tone="ok" sub="tap to trace ↗" />
            </button>
          ))}
        </div>
        {trace && (
          <div data-testid="revenue-trace-panel" className="mt-3 rounded-xl border border-[#F2B705]/30 bg-[#0A0A0A] p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-white">{trace.label} — {money(trace.revenue)}</p>
                <p className="text-white/40 text-[11px] mt-0.5">{trace.range.start} → {trace.range.end} · {trace.range.timezone} · {trace.range.week_rule}</p>
                <p className="text-white/40 text-[11px]">{trace.proof}</p>
              </div>
              <button onClick={() => setTrace(null)} className="text-white/40 hover:text-white text-lg leading-none">✕</button>
            </div>
            <div className="mt-3 space-y-1 max-h-80 overflow-y-auto">
              {trace.rows.length === 0 && <p className="text-white/35 text-sm">No transactions in this period.</p>}
              {trace.rows.map((r) => (
                <div key={r.transaction_id} data-testid={`trace-row-${r.transaction_id}`}
                  className={`flex flex-wrap items-center justify-between gap-2 rounded px-3 py-1.5 text-xs border ${r.reversed ? "border-red-500/25 bg-red-500/[0.04]" : r.counted ? "border-white/5 bg-white/[0.02]" : "border-white/5 opacity-50"}`}>
                  <span className="text-white/75">{r.timestamp} · <b>{r.customer || "—"}</b> · {r.source}{r.method ? ` · ${r.method}` : ""} <span className="text-white/30">({r.transaction_id})</span>
                    {r.reversed && <span data-testid={`trace-reversed-badge-${r.transaction_id}`} className="ml-2 text-[9px] font-bold text-red-300 border border-red-500/40 rounded px-1.5 py-0.5">REVERSED — NOT COUNTED</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className={`font-bold ${r.reversed ? "text-white/35 line-through" : r.amount < 0 ? "text-red-400" : r.counted ? "text-green-400" : "text-white/40 line-through"}`}>{money(r.amount)}</span>
                    <span className="text-white/35">{r.reason}</span>
                    {r.can_reverse && (
                      <button data-testid={`trace-reverse-${r.transaction_id}`} onClick={() => reverseFromTrace(r)}
                        title="Management only — bookkeeping correction, no money moves"
                        className="text-[9px] font-bold text-white/40 border border-white/15 rounded px-1.5 py-0.5 hover:text-red-300 hover:border-red-400/50 transition-colors">
                        Reverse Entry
                      </button>
                    )}
                    {r.reversed && (
                      <button data-testid={`trace-undo-reversal-${r.transaction_id}`} onClick={() => undoFromTrace(r)}
                        title="Management only — reactivates this exact payment"
                        className="text-[9px] font-bold text-emerald-300/80 border border-emerald-500/30 rounded px-1.5 py-0.5 hover:text-emerald-200 hover:border-emerald-400/60 transition-colors">
                        Undo Reversal
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-2">Profitability</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric testid="fd-manual-revenue" label="Manual Revenue" value={money(d.manual_revenue_total)} sub={`${money(d.manual_revenue_month)} this month · ${money(d.manual_insurance_total)} insurance (not AutoHire)`} icon={HandCoins} tone="gold" />
          <Metric testid="fd-expenses" label="Expenses (Year)" value={money(d.expenses_year)} sub={`${money(d.expenses_month)} this month`} icon={Receipt} tone="danger" />
          <Metric testid="fd-net-profit" label="Est. Net Profit (Year)" value={money(d.net_profit_year)} icon={PiggyBank} tone={d.net_profit_year >= 0 ? "gold" : "danger"} />
          <Metric testid="fd-outstanding" label="Outstanding Balances" value={money(d.outstanding_balance)} sub={`${d.outstanding_count} booking(s) owe`} icon={AlertCircle} tone={d.outstanding_balance > 0 ? "warn" : "ok"} />
        </div>
        <div className="flex justify-end mt-2">
          <button data-testid="fd-zero-balances" onClick={zeroBalances}
            className="flex items-center gap-1.5 text-xs font-semibold border border-red-500/40 text-red-400 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors">
            <Eraser size={13} /> Zero Out All Balances (Management)
          </button>
        </div>
        {d.allocation_month && (
          <div className="mt-4" data-testid="fd-allocation">
            <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Where The Money Came From — This Month</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <Metric testid="fd-alloc-gross" label="Gross Collected" value={money(d.allocation_month.gross_collected)} icon={HandCoins} tone="ok" />
              <Metric testid="fd-alloc-autohire" label="AutoHire Revenue (excl. insurance)" value={money(d.allocation_month.autohire_revenue)} icon={Car} tone="gold" />
              <Metric testid="fd-alloc-protection" label="All-In Insurance Collected (third-party)" value={money(d.allocation_month.protection_collected)} sub="not AutoHire revenue" icon={PiggyBank} />
              <Metric testid="fd-alloc-unallocated" label="Unallocated (Legacy All-In)" value={money(d.allocation_month.unallocated_legacy)} sub="no reliable rate — never guessed" icon={Clock} tone={d.allocation_month.unallocated_legacy > 0 ? "warn" : "ok"} />
              <Metric testid="fd-alloc-settled" label="Insurance Settled to Provider" value={money(d.allocation_month.insurance_settled_to_provider)} sub="manual only — never auto-created" icon={Landmark} />
              <Metric testid="fd-alloc-base" label="Base Rental Revenue" value={money(d.allocation_month.base_rental)} icon={Car} />
              <Metric testid="fd-alloc-waiver" label="Damage Waiver" value={money(d.allocation_month.damage_waiver)} icon={Receipt} />
              <Metric testid="fd-alloc-driver" label="Driver Fees" value={money(d.allocation_month.driver_fees)} icon={Wallet} />
              <Metric testid="fd-alloc-other" label="Other Fees" value={money(d.allocation_month.other_fees)} icon={Receipt} />
              <Metric testid="fd-alloc-legacy" label="Legacy / Unclassified" value={money(d.allocation_month.unclassified_legacy)} icon={Clock} />
              <Metric testid="fd-alloc-refunds" label="Refunds / Credits" value={money(d.allocation_month.refunds_credits)} icon={AlertCircle} tone={d.allocation_month.refunds_credits > 0 ? "warn" : "ok"} />
              <Metric testid="fd-alloc-expenses" label="Expenses" value={money(d.allocation_month.expenses)} icon={Landmark} tone={d.allocation_month.expenses > 0 ? "warn" : "ok"} />
              <Metric testid="fd-alloc-net" label="Net Profit" value={money(d.allocation_month.net_profit)} sub="AutoHire revenue − expenses (insurance excluded, never double-deducted)" icon={TrendingUp} tone={d.allocation_month.net_profit >= 0 ? "ok" : "warn"} />
            </div>
            <p className="text-white/30 text-[10px] mt-1.5">
              All-In third-party insurance is collected from renters but is never AutoHire's own earnings — it's shown
              separately above, not inside AutoHire Revenue or Net Profit. Full breakdown with custom dates &amp;
              per-vehicle/customer/method grouping → Revenue Report tab.
            </p>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-2">Deposits &amp; Fleet</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Metric testid="fd-undeposited" label="Awaiting Deposit" value={money(d.undeposited_amount)} sub={`${d.undeposited_count} payment(s)`} icon={Clock} tone="warn" />
          <Metric testid="fd-pending-deposits" label="Pending Deposits" value={money(d.pending_deposits_amount)} sub={`${d.pending_deposits_count} deposit(s)`} icon={Landmark} tone="warn" />
          <Metric testid="fd-deposited-week" label="Deposited This Week" value={money(d.deposited_this_week)} icon={Landmark} tone="ok" />
          <Metric testid="fd-avg-rental" label="Avg / Rental" value={money(d.avg_revenue_per_rental)} icon={Gauge} />
          <Metric testid="fd-fleet-lifetime" label="Fleet Lifetime Revenue" value={money(d.fleet_lifetime_revenue)} sub={`${money(d.avg_revenue_per_vehicle)} avg per vehicle`} icon={Car} tone="gold" />
        </div>
      </div>
    </div>
  );
}
